import { walkPlan } from '@timelog/pm-shared';
import type { PlanNode, ProjectDocument } from '@timelog/pm-shared';

/**
 * After a PUT, the response document carries server-assigned codes (BR-6) and
 * bumped codeCounters. Adopt exactly those onto the CURRENT local document —
 * never names/hours/dates, which the user may have edited while the PUT was
 * in flight. Everything here is iterative (BR-3).
 */
export const adoptServerCodes = (
  current: ProjectDocument,
  server: ProjectDocument,
): ProjectDocument => {
  const codeById = new Map<string, string>();
  walkPlan(server.plan, (node) => {
    if (node.code !== undefined) codeById.set(node.id, node.code);
  });

  // Post-order rebuild with structural sharing: copy a node only when its own
  // code changes or a descendant changed.
  type Frame = { node: PlanNode; childIndex: number; children: PlanNode[]; changed: boolean };

  const rebuilt: PlanNode[] = [];
  let anyChanged = false;

  for (const root of current.plan) {
    const stack: Frame[] = [{ node: root, childIndex: 0, children: [], changed: false }];
    let completed: PlanNode | null = null;

    while (stack.length > 0) {
      const frame = stack[stack.length - 1]!;
      if (completed !== null) {
        frame.children.push(completed);
        completed = null;
      }
      if (frame.childIndex < frame.node.children.length) {
        const child = frame.node.children[frame.childIndex]!;
        frame.childIndex += 1;
        stack.push({ node: child, childIndex: 0, children: [], changed: false });
      } else {
        stack.pop();
        const serverCode = codeById.get(frame.node.id);
        const needsCode = serverCode !== undefined && frame.node.code !== serverCode;
        const node =
          frame.changed || needsCode
            ? {
                ...frame.node,
                ...(needsCode ? { code: serverCode } : {}),
                children: frame.changed ? frame.children : frame.node.children,
              }
            : frame.node;
        if (node !== frame.node && stack.length > 0) stack[stack.length - 1]!.changed = true;
        if (stack.length === 0) {
          rebuilt.push(node);
          if (node !== root) anyChanged = true;
        } else {
          completed = node;
        }
      }
    }
  }

  const countersChanged =
    current.codeCounters.deliverable !== server.codeCounters.deliverable ||
    current.codeCounters.task !== server.codeCounters.task;

  if (!anyChanged && !countersChanged) return current;

  return {
    ...current,
    plan: anyChanged ? rebuilt : current.plan,
    codeCounters: countersChanged ? server.codeCounters : current.codeCounters,
  };
};
